import { forwardRef, useCallback, useContext, useMemo } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { ButtonPrimary, LabelBold, Box } from '@comeon/ui';
import ConfigurationContext from '../../../../configuration/configuration-context.js';
import { TabBar } from '../../../tab-bar.js';
import useTranslations from '../../../translation/use-translations';
import findAttributeValue from '../../../../utils/find-attr-value.js';
import useContextualPathTransformer from '../../../../hooks/use-contextual-path-transformer.js';

import TabItemLink from '../components/tab-item-link.js';
import {
	ROUTE_WE_SPIN_GAME_ROOM_BUY_IN,
	ROUTE_WE_SPIN_GAME_ROOM_TIP,
	ROUTE_WE_SPIN_GAME_ROOM_PREVIEW,
	ROUTE_WE_SPIN_GAME_VIEW,
	ROUTE_WE_SPIN_GAME_ROOM_RULES,
	ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST,
	ROUTE_WE_SPIN_GAME_ROOM_CHAT,
	ROUTE_WE_SPIN_GAME_ROOM_INFO,
	ROUTE_WE_SPIN_GAME_ROOM_ABS
} from '../constants/we-spin-route-constants.js';
import WE_SPIN_GAME_ROOM_TAB_ITEMS from './we-spin-game-room-tab-items.js';
import StartTime from '../components/start-time.js';
import { WE_SPIN_GAME_ROOM_FOOTER_TEXT_KEYS } from '../constants/we-spin-text-keys.js';
import { selectConfiguration, selectSessionForStreamRef } from '../ducks/we-spin-selectors.js';
import { SESSION_OFFLINE_RESULT } from '../constants/we-spin-stream-states.js';
import { useMatch, useNavigate } from 'react-router-dom';

const BoxWithBackgroundColor = styled(Box)`
	background-color: var(--we-spin-preview-stream-bg-color);
`;

const WeSpinGameRoomFooter = forwardRef(({ streamView, location }, ref) => {
	// TODO: this is not just path, but path pattern, so need to construct it manually, is this really needed?
	const match = useMatch(`${ROUTE_WE_SPIN_GAME_ROOM_ABS}/:segment1/:ref`);
	const pathPattern = useMemo(
		() => match?.pattern?.path.replace(':segment1', match.params.segment1),
		[match?.pattern?.path, match.params.segment1]
	);
	const navigate = useNavigate();
	const { hasWeSpinTipping } = useContext(ConfigurationContext);
	const { translations: tr } = useTranslations(WE_SPIN_GAME_ROOM_FOOTER_TEXT_KEYS);
	const { wespinChatDisabled } = useSelector(selectConfiguration);

	const { streamRef, start } = streamView;

	const {
		sessionRef,
		sessionState,
		joined: sessionJoined
	} = useSelector(selectSessionForStreamRef(streamRef)) || {
		sessionRef: streamView.streamRef,
		sessionState: streamView.state,
		sessionJoined: false
	};

	const transformPath = useContextualPathTransformer();
	const redirectToGameRoom = useCallback(() => {
		const link = transformPath(`${ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST}/${sessionRef}`, {
			location: {
				pathname: `${ROUTE_WE_SPIN_GAME_VIEW}/${sessionRef}`
			}
		});
		navigate(link, { replace: true });
	}, [navigate, sessionRef, transformPath]);

	const clickTabItem = useCallback(
		event => {
			event.preventDefault();
			event.stopPropagation();
			const value = findAttributeValue(event.target, 'data-tag');
			if (value) {
				navigate(transformPath(value), { replace: true });
			}
		},
		[navigate, transformPath]
	);

	const FILTERED_TAB_ITEMS = useMemo(
		() =>
			WE_SPIN_GAME_ROOM_TAB_ITEMS.filter(
				({ link }) =>
					hasWeSpinTipping ||
					[
						ROUTE_WE_SPIN_GAME_ROOM_TIP,
						...(wespinChatDisabled ? [ROUTE_WE_SPIN_GAME_ROOM_CHAT] : [])
					].indexOf(link) === -1
			),
		[hasWeSpinTipping, wespinChatDisabled]
	);

	const isInGameRoom = location.pathname.startsWith(ROUTE_WE_SPIN_GAME_VIEW);
	return pathPattern.startsWith(ROUTE_WE_SPIN_GAME_ROOM_RULES) ? null : ((streamView.joined &&
			pathPattern === `${ROUTE_WE_SPIN_GAME_ROOM_PREVIEW}/:ref`) ||
			(sessionJoined &&
				sessionState !== SESSION_OFFLINE_RESULT &&
				pathPattern === `${ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST}/:ref`)) &&
	  !isInGameRoom &&
	  !streamView.isPrivateStreamer ? (
		<BoxWithBackgroundColor flex="1" mb="0" p="5" className="wespin-gameroom-footer-prejoin">
			<LabelBold mb="5" className="u-text-c">
				{tr.WE_SPIN_GAME_ROOM_FOOTER_PEEK}
			</LabelBold>
			<ButtonPrimary onClick={redirectToGameRoom}>
				<StartTime start={start} joined={streamView.joined} />
			</ButtonPrimary>
		</BoxWithBackgroundColor>
	) : (pathPattern === `${ROUTE_WE_SPIN_GAME_ROOM_EVENT_LIST}/:ref` && isInGameRoom) ||
	  pathPattern === `${ROUTE_WE_SPIN_GAME_ROOM_INFO}/:ref` ||
	  pathPattern === `${ROUTE_WE_SPIN_GAME_ROOM_CHAT}/:ref` ? (
		<>
			<div ref={ref} className="wespin-gameroom-footer-stream-preview" />
			<TabBar data-at="tabs">
				{FILTERED_TAB_ITEMS.map(({ link, labelKey, icon }) => (
					<TabItemLink
						key={link}
						label={tr[labelKey]}
						icon={icon}
						selected={location.pathname.startsWith(link)}
						data-tag={`${link}/${streamRef}`}
						onClick={clickTabItem}
					/>
				))}
			</TabBar>
		</>
	) : pathPattern === `${ROUTE_WE_SPIN_GAME_ROOM_BUY_IN}/:ref` ? (
		<div ref={ref} />
	) : null;
});

export default WeSpinGameRoomFooter;
